import type { CameraState, Size } from '../types';
import {
  clampPitch,
  createProjector,
  getCameraBasis,
} from '../geometry/projection';
import type { SurfaceMesh } from '../geometry/surfaceMesh';
import type { ColorScale } from './colorScale';
import { toDrapedGridCoverage, toGridInk, type DrapedGrid } from './drapedGrid';
import { samplePaint, type SurfacePaint } from './surfacePaint';
import { createRampLookup } from './rampLookup';
import {
  accumulateShading,
  addGroundBounce,
  DEFAULT_LIGHTING,
  resolveLights,
  type LightingModel,
} from './lighting';

/** One projected vertex, with everything interpolated across a triangle. */
type ProjectedVertex = {
  screenX: number;
  screenY: number;
  /** Larger is nearer the eye, so the depth test keeps the maximum. */
  depth: number;
  /**
   * The reciprocal of the homogeneous divisor at this vertex.
   *
   * `getForeshortening` is `1 / (1 - k·depth)`, and the GPU's `clip.w` is that same divisor, so
   * this is `1/w` - which is exactly the weight a perspective-correct interpolation needs.
   */
  inverseW: number;
  value: number;
  normalX: number;
  normalY: number;
  normalZ: number;
  /** Position on the plot, in the cube, for the draped grid. */
  cubeX: number;
  cubeY: number;
};

export type RasterizeMeshArgs = {
  mesh: SurfaceMesh;
  camera: CameraState;
  viewport: Size;
  colorScale: ColorScale;
  lighting?: LightingModel;
  /** Mixes between the flat ramp colour (0) and fully lit (1). */
  shading: number;
  /**
   * Renders at a fraction of the viewport and is scaled up on paint.
   *
   * Per-pixel shading in JavaScript costs real time, and an orbit only needs to be legible while it
   * moves. A half-resolution buffer during a drag keeps the interaction responsive, and the
   * full-resolution pass lands when it settles.
   */
  scale: number;
  /** Axis grid lines drawn onto the surface; null draws none. */
  drapedGrid: DrapedGrid | null;
  /** Colors the surface in place of the ramp; null colors it by height. */
  paint: SurfacePaint | null;
  /** Bands each light's diffuse term is rounded to, for cel shading; 0 leaves it smooth. */
  celSteps: number;
};

/**
 * Whether a triangle shows its front to the eye, from its signed area on screen.
 *
 * Screen y grows downward, so a triangle counter-clockwise seen from its front has negative area.
 */
export const isFrontFacing = (area: number): boolean => area < 0;

/**
 * Rasterises a triangle mesh with per-pixel Phong shading and a depth buffer.
 *
 * Takes a mesh rather than a grid, which is what lets the same code draw a uniform lattice and an
 * adaptively refined one - and is the layout a GPU pipeline wants, so the eventual hardware path
 * consumes the identical buffers rather than a repacking of them.
 *
 * Returns null when there is nothing to draw, so the caller can skip the composite.
 */
/**
 * The frame's pixel buffer and depth buffer, reused across paints rather than allocated per call.
 *
 * 4.32 MB each at 1200x900, on the path where a frame already costs about 49 ms - the same
 * argument, and the same size, as the scratch canvas `renderPhongFill` holds for the composite.
 * Hoisting that one took out a third of this function's churn and left the two either side of it.
 *
 * Safe to share: `rasterizeMesh` is synchronous and both buffers are fully initialised before they
 * are read - the depth buffer by the fill below, and every pixel of the image either written by a
 * covered triangle or cleared here - so no frame can observe another's.
 *
 * Shared by every caller, which is safe for the same reason `renderPhongFill`'s scratch canvas is:
 * that function is synchronous from here through `putImageData` to `drawImage`, so a second
 * chart's frame cannot begin until the first has finished reading these bytes. The safety is a
 * property of that call chain rather than of this holder - an `await` between the rasterise and
 * the composite would break it, and nothing here would say so.
 *
 * Keyed on exact dimensions, so two software surface charts of *different* sizes repainting in
 * turn get no reuse at all and allocate as they did before this existed. That is a lost saving
 * rather than a regression, and the alternative - reusing a larger buffer through a subarray -
 * would put a capacity and a length in every clear. Worth revisiting if a dashboard ever puts two
 * differently-sized software surfaces side by side.
 */
const scratch: { image: ImageData | null; depth: Float32Array | null } = {
  image: null,
  depth: null,
};

const getScratchImage = (width: number, height: number): ImageData => {
  if (
    !scratch.image ||
    scratch.image.width !== width ||
    scratch.image.height !== height
  ) {
    scratch.image = new ImageData(width, height);
    return scratch.image;
  }
  /* Cleared rather than reallocated. A pixel no triangle covers has to be transparent, and last
     frame's surface is exactly what would show through otherwise. */
  scratch.image.data.fill(0);
  return scratch.image;
};

const getScratchDepth = (length: number): Float32Array => {
  if (!scratch.depth || scratch.depth.length !== length) {
    scratch.depth = new Float32Array(length);
  }
  scratch.depth.fill(-Infinity);
  return scratch.depth;
};

export type RasterizedMesh = {
  image: ImageData;
  /**
   * View-axis depth per pixel, larger nearer the eye, `-Infinity` where nothing was drawn. Borrowed
   * like the image: refilled by the next call.
   */
  depth: Float32Array;
  /** Half the extent of the camera's cube along the view axis, which bounds `depth`. */
  depthExtent: number;
};

/**
 * Not exported from the package, because what it returns is borrowed.
 *
 * The image and the depth buffer are held across calls and refilled by the next one, so the
 * returned `ImageData` is only valid until then. `renderPhongFill` composites it synchronously
 * before returning, which makes it safe - but that is a property of the caller rather than of
 * this, and a consumer holding one past its frame would find it repainted underneath them. With
 * one production caller the borrow is an invariant someone can check; on the public surface it
 * would be a contract nobody was told about.
 */

export const rasterizeMesh = (args: RasterizeMeshArgs): ImageData | null =>
  rasterizeMeshWithDepth(args)?.image ?? null;

export const rasterizeMeshWithDepth = ({
  mesh,
  camera,
  viewport,
  colorScale,
  lighting = DEFAULT_LIGHTING,
  shading,
  scale,
  drapedGrid,
  paint,
  celSteps,
}: RasterizeMeshArgs): RasterizedMesh | null => {
  if (mesh.triangleCount === 0) return null;

  const width = Math.max(1, Math.floor(viewport.width * scale));
  const height = Math.max(1, Math.floor(viewport.height * scale));

  const project = createProjector(camera, { width, height });
  const basis = getCameraBasis(camera.yaw, clampPitch(camera.pitch));
  /* Half-extent of the cube the camera is handed, which bounds |depth| and so gives a stable
     0-to-1 nearness for the fog without a pass over the vertices to find the true range. */
  const depthExtent = Math.hypot(1, 1, camera.heightScale);

  /* Everything below is constant for the frame and was being rebuilt per covered pixel - roughly a
     million of them on a filled surface at scale 1, which is what the 49 ms measurement is made
     of. The lights resolve against the camera basis, not against the surface; the ramp has at most
     `bands` distinct outputs and `forValue` costs nine `Math.pow` calls and an array each time. */
  const lights = resolveLights(lighting, basis);
  const fogStrength = Math.max(0, lighting.fog.strength);

  /* A banded scale comes back bit-identical to the previous per-pixel `forValue`, since the table
     holds every colour it could return. A continuous one does not: 256 entries with a linear blend
     between them approximates a curve, and measured against `forValue` over a filled 460x340
     surface it differs by one unit in 255 on 6% of channels and never by more. */
  const ramp = createRampLookup(colorScale);

  /* Reused across pixels rather than returned fresh: at this call count the allocation is the
     cost, not the arithmetic. */
  const normal: [number, number, number] = [0, 0, 0];
  const base: [number, number, number] = [0, 0, 0];
  const lit: [number, number, number] = [0, 0, 0];
  const intensity: [number, number, number] = [0, 0, 0];
  const highlight: [number, number, number] = [0, 0, 0];

  const image = getScratchImage(width, height);
  const pixels = image.data;
  /* -Infinity rather than 0: depth is a dot product against the view axis and is negative behind
     the plot's centre, so a zero-initialised buffer would reject the far half of the surface as
     though something already occluded it. */
  const depthBuffer = getScratchDepth(width * height);

  /* Every vertex projected once, not once per triangle that references it - which is the point of
     an indexed mesh, and matters more on a refined mesh where a crest vertex can touch a dozen
     triangles. */
  const projected: ProjectedVertex[] = Array.from(
    { length: mesh.vertexCount },
    (_, index) => {
      const at = index * 3;
      const point = project(
        mesh.positions[at],
        mesh.positions[at + 1],
        mesh.positions[at + 2]
      );

      return {
        screenX: point.screenX,
        screenY: point.screenY,
        depth: point.depth,
        inverseW: point.scale,
        value: mesh.values[index],
        normalX: mesh.normals[at],
        normalY: mesh.normals[at + 1],
        normalZ: mesh.normals[at + 2],
        cubeX: mesh.positions[at],
        cubeY: mesh.positions[at + 1],
      };
    }
  );

  const drawTriangle = (
    a: ProjectedVertex,
    b: ProjectedVertex,
    c: ProjectedVertex
  ): void => {
    const minX = Math.max(
      0,
      Math.floor(Math.min(a.screenX, b.screenX, c.screenX))
    );
    const maxX = Math.min(
      width - 1,
      Math.ceil(Math.max(a.screenX, b.screenX, c.screenX))
    );
    const minY = Math.max(
      0,
      Math.floor(Math.min(a.screenY, b.screenY, c.screenY))
    );
    const maxY = Math.min(
      height - 1,
      Math.ceil(Math.max(a.screenY, b.screenY, c.screenY))
    );
    if (minX > maxX || minY > maxY) return;

    const area =
      (b.screenX - a.screenX) * (c.screenY - a.screenY) -
      (c.screenX - a.screenX) * (b.screenY - a.screenY);
    /* Zero area after projection: an edge-on triangle covers no pixels and would divide by zero
       computing its barycentric weights. */
    if (area === 0) return;
    const isUnderside = !isFrontFacing(area);
    const facing = isUnderside ? -1 : 1;

    /* How far the plot position moves per pixel across this triangle - the software `fwidth`. From
       the screen-space barycentric gradients, so affine rather than perspective-correct, which is
       below a pixel's difference at the triangle sizes a surface is drawn with. */
    const w0PerX = (b.screenY - c.screenY) / area;
    const w0PerY = (c.screenX - b.screenX) / area;
    const w1PerX = (c.screenY - a.screenY) / area;
    const w1PerY = (a.screenX - c.screenX) / area;
    const footprintX =
      Math.abs(w0PerX * (a.cubeX - c.cubeX) + w1PerX * (b.cubeX - c.cubeX)) +
      Math.abs(w0PerY * (a.cubeX - c.cubeX) + w1PerY * (b.cubeX - c.cubeX));
    const footprintY =
      Math.abs(w0PerX * (a.cubeY - c.cubeY) + w1PerX * (b.cubeY - c.cubeY)) +
      Math.abs(w0PerY * (a.cubeY - c.cubeY) + w1PerY * (b.cubeY - c.cubeY));

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        /* Sampled at the pixel centre, which is what stops adjacent triangles both claiming a
           boundary pixel and leaving a seam of background between them. */
        const px = x + 0.5;
        const py = y + 0.5;

        const w0 =
          ((b.screenX - px) * (c.screenY - py) -
            (c.screenX - px) * (b.screenY - py)) /
          area;
        const w1 =
          ((c.screenX - px) * (a.screenY - py) -
            (a.screenX - px) * (c.screenY - py)) /
          area;
        const w2 = 1 - w0 - w1;
        if (w0 < 0 || w1 < 0 || w2 < 0) continue;

        /* Affine on purpose, and the one quantity here that is. The hardware writes
           `normalisedDepth * clipW` into clip z and divides by w, so the depth the GPU tests is an
           affine function of this one interpolated linearly in screen space. Making it
           perspective-correct would put the two depth buffers out of step. */
        const depth = w0 * a.depth + w1 * b.depth + w2 * c.depth;
        const index = y * width + x;
        if (depth <= depthBuffer[index]) continue;
        depthBuffer[index] = depth;

        /* Everything the fragment reads is perspective-correct, because the shader's varyings
           carry no `@interpolate` qualifier and WGSL defaults them to `perspective`. Screen-space
           barycentrics reweighted by 1/w is what that means; skipped, the two renderers shade the
           interior of any triangle spanning a depth range differently, and at the default camera
           a band boundary on a plot-spanning triangle lands more than a full band away from the
           GPU's. Which is right is not a toss-up: the markers, wireframe, drop lines and isolines
           are placed at the true screen position of a 3D point, so only the corrected weights put
           the value belonging to that point under that pixel. */
        const s0 = w0 * a.inverseW;
        const s1 = w1 * b.inverseW;
        const s2 = w2 * c.inverseW;
        const inverseSum = 1 / (s0 + s1 + s2);
        const p0 = s0 * inverseSum;
        const p1 = s1 * inverseSum;
        const p2 = s2 * inverseSum;

        /* The normal interpolated per pixel and renormalised - this is what makes it Phong rather
           than Gouraud, which interpolates the resulting colour and loses any highlight falling
           between vertices. */
        const nx = p0 * a.normalX + p1 * b.normalX + p2 * c.normalX;
        const ny = p0 * a.normalY + p1 * b.normalY + p2 * c.normalY;
        const nz = p0 * a.normalZ + p1 * b.normalZ + p2 * c.normalZ;
        const length = (Math.hypot(nx, ny, nz) || 1) * facing;

        normal[0] = nx / length;
        normal[1] = ny / length;
        normal[2] = nz / length;
        accumulateShading(
          normal,
          lights,
          lighting.ambient,
          lighting.shininess,
          intensity,
          highlight,
          celSteps
        );
        if (isUnderside)
          addGroundBounce(normal[2], lighting.ambient, intensity);

        if (paint) {
          samplePaint(
            paint,
            p0 * a.cubeX + p1 * b.cubeX + p2 * c.cubeX,
            p0 * a.cubeY + p1 * b.cubeY + p2 * c.cubeY,
            base
          );
        } else {
          ramp.into(p0 * a.value + p1 * b.value + p2 * c.value, base);
        }

        /* Nearness from the interpolated depth, so aerial perspective varies within a triangle
           rather than stepping at its edges. Perspective-correct rather than reusing the affine
           depth above: the shader fogs from its `depth` varying, which the hardware interpolates
           the corrected way, while the depth *test* is the affine one. */
        const fogDepth = p0 * a.depth + p1 * b.depth + p2 * c.depth;
        const nearness = (fogDepth + depthExtent) / (2 * depthExtent);
        const fogAmount =
          fogStrength * (1 - Math.min(1, Math.max(0, nearness)));

        const gridAmount = drapedGrid
          ? Math.max(
              toDrapedGridCoverage(
                p0 * a.cubeX + p1 * b.cubeX + p2 * c.cubeX,
                drapedGrid.x,
                footprintX
              ),
              toDrapedGridCoverage(
                p0 * a.cubeY + p1 * b.cubeY + p2 * c.cubeY,
                drapedGrid.y,
                footprintY
              )
            ) * drapedGrid.opacity
          : 0;

        const offset = index * 4;
        for (let channel = 0; channel < 3; channel++) {
          /* The shading dial mixes the terms rather than the resulting colours, so 0 is exactly
             the flat ramp colour rather than approximately it - the same arithmetic
             `scaleShading` does, inlined to keep the allocation out of the pixel loop. */
          const scaledIntensity = 1 + (intensity[channel] - 1) * shading;
          lit[channel] = Math.min(
            255,
            Math.max(
              0,
              Math.round(
                base[channel] * scaledIntensity +
                  highlight[channel] * shading * 255
              )
            )
          );
        }
        const gridInk = drapedGrid ? toGridInk(lit, drapedGrid) : null;
        for (let channel = 0; channel < 3; channel++) {
          /* Inked onto the surface before the fog, so the grid recedes with what it is drawn on. */
          const inked = gridInk
            ? lit[channel] + (gridInk[channel] - lit[channel]) * gridAmount
            : lit[channel];
          pixels[offset + channel] = Math.round(
            inked + (lighting.fog.color[channel] * 255 - inked) * fogAmount
          );
        }
        pixels[offset + 3] = 255;
      }
    }
  };

  for (let triangle = 0; triangle < mesh.indices.length; triangle += 3) {
    drawTriangle(
      projected[mesh.indices[triangle]],
      projected[mesh.indices[triangle + 1]],
      projected[mesh.indices[triangle + 2]]
    );
  }

  return { image, depth: depthBuffer, depthExtent };
};
